import React from 'react';
import Loader from 'components/atoms/loader';
import AiManagementCollapsibleCard from 'components/molecules/ai_management_collapsible_card';
import AiManagementDrawer from 'components/molecules/ai_management_drawer';
import { usePossibleCompetitorCompanyContracts } from 'hooks/usePossibleCompetitorCompanyContracts';
import {
  PossibleCompetitorCompanyContract,
  PossibleCompetitorSize,
} from 'services/actions/ai_management_possible_competitors';
import { EMPTY_VALUE, dateBr, money, plural } from 'services/ai_management_format';
import { maskCnpj } from 'utils/cnpj';
import { Content } from './styles';

interface AiManagementCompetitorContractsDrawerProps {
  biddingId: number | null;
  cnpj: string;
  name: string | null;
  size: PossibleCompetitorSize;
  onClose: () => void;
}

const yearOf = (contract: PossibleCompetitorCompanyContract) =>
  contract.ano ??
  (Number((contract.assinatura ?? contract.vigencia_inicio ?? '').slice(0, 4)) || 0);

const AiManagementCompetitorContractsDrawer: React.FC<
  AiManagementCompetitorContractsDrawerProps
> = ({ biddingId, cnpj, name, size, onClose }) => {
  const biddingIds = Array.from(new Set(size.contratados.flatMap((year) => year.licitacoes))).sort(
    (a, b) => a - b,
  );
  const { contracts, loading, error } = usePossibleCompetitorCompanyContracts(
    biddingId,
    cnpj,
    biddingIds,
  );
  const years = Array.from(new Set(contracts.map(yearOf))).sort((a, b) => b - a);

  const renderYear = (ano: number) => {
    const rows = contracts.filter((contract) => yearOf(contract) === ano);
    const total = rows.reduce((sum, contract) => sum + (contract.valor ?? 0), 0);
    return (
      <AiManagementCollapsibleCard
        key={ano}
        title={String(ano)}
        className="mb-3"
        bodyClassName="p-0"
        aside={
          <>
            <span className="text-muted mr-3">{plural(rows.length, 'contrato', 'contratos')}</span>
            <span className="font-weight-bold year-total">{money(total)}</span>
          </>
        }
      >
        <div className="table-responsive">
          <table className="table bg-white mb-0 contracts-table">
            <thead className="thead-light">
              <tr>
                <th scope="col">Licitação e órgão</th>
                <th scope="col" className="text-nowrap">
                  Assinatura
                </th>
                <th scope="col" className="text-nowrap">
                  Valor contratado
                </th>
                <th scope="col" className="text-center">
                  Documentos
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((contract) => (
                <tr key={`${contract.licitacao.id}-${contract.id ?? contract.numero}`}>
                  <td className="contract-cell">
                    <div className="text-primary text-nowrap">
                      {contract.licitacao.edital ?? `Nº ${contract.licitacao.id ?? EMPTY_VALUE}`}
                    </div>
                    <div className="contract-orgao" title={contract.licitacao.orgao ?? undefined}>
                      {contract.licitacao.orgao ?? EMPTY_VALUE}
                    </div>
                    {contract.licitacao.cidade ? (
                      <div className="text-muted text-nowrap">{contract.licitacao.cidade}</div>
                    ) : null}
                  </td>
                  <td className="text-nowrap">
                    <div>{dateBr(contract.assinatura ?? contract.vigencia_inicio)}</div>
                    {contract.vigencia_fim ? (
                      <div className="text-muted">até {dateBr(contract.vigencia_fim)}</div>
                    ) : null}
                  </td>
                  <td className="text-nowrap font-weight-bold">{money(contract.valor)}</td>
                  <td className="text-center contract-docs">
                    {contract.arquivos.length ? (
                      contract.arquivos.map((file) => (
                        <a
                          key={file.id ?? file.url}
                          className="btn btn-outline-secondary btn-sm m-1"
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          title={file.titulo}
                          aria-label={`Baixar ${file.titulo}`}
                        >
                          <i className="material-icons">file_download</i>
                        </a>
                      ))
                    ) : (
                      <span className="text-muted">Sem documento publicado</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AiManagementCollapsibleCard>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="py-5">
          <Loader />
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          Não foi possível carregar os contratos desta empresa.
        </div>
      );
    }
    if (!years.length) {
      return <div className="text-muted">Nenhum contrato encontrado nas bases públicas.</div>;
    }
    return years.map(renderYear);
  };

  return (
    <AiManagementDrawer
      variant="side"
      title="Contratos"
      subtitle={
        <>
          {name ?? 'Empresa'}
          <span className="mx-2">•</span>
          {maskCnpj(cnpj)}
        </>
      }
      onClose={onClose}
    >
      <Content>
        <div className="contracts-notice d-flex align-items-start border rounded px-3 py-3 mb-4">
          <i className="material-icons text-muted mr-2">info</i>
          <div>
            O levantamento considera só os contratos celebrados disponíveis nas bases públicas, como
            o Compras.gov.br e o PNCP, e pode divergir das adjudicações.
          </div>
        </div>
        {renderBody()}
      </Content>
    </AiManagementDrawer>
  );
};

export default AiManagementCompetitorContractsDrawer;
